import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'

import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import {
  parseQueryParams,
  serializeQueryParams,
} from '../../hooks/useQueryParam'

export type UseIdeaComposerQueryData = {
  source: CTA
  title?: string
  text?: string
  media?: string[]
  tags?: string[]
  groupId?: string
  placeAfterId?: string
  aiAssisted?: 'true' | 'false'
  duplicate?: boolean
  // Allow providing a basePath to use instead of the current location.pathname
  // Newer React Router navigations batch location updates and only propogate the last one
  // so it might be necessary to provide the basePath to ensure the composer is opened with the correct path
  basePath?: string
  templateId?: string
}

export type IdeaComposerLocationState = {
  returnTo?: Location['pathname']
  action: 'create' | 'edit' | 'duplicate'
  basePath?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function isIdeaComposerLocationState(
  value: unknown,
): value is IdeaComposerLocationState {
  if (!isRecord(value)) return false

  const hasValidAction =
    value.action === 'create' ||
    value.action === 'edit' ||
    value.action === 'duplicate'

  return (
    (value.returnTo === undefined || typeof value.returnTo === 'string') &&
    hasValidAction &&
    (value.basePath === undefined || typeof value.basePath === 'string')
  )
}

export type UseIdeaComposerReturn = {
  createIdeaWithComposer: (input: UseIdeaComposerQueryData) => void
  editIdeaWithComposer: (input: { source: CTA; ideaId: string }) => void
}

export type CreateIdeaWithComposer = (input: UseIdeaComposerQueryData) => void

/**
 * Helper hook used to open IdeaCreationDialog and IdeaEditDialog correctly.
 * It redirects to the correct route, manages the query params,
 * and stores the returnTo location for closing the composer.
 *
 * @example
 * const { editIdeaWithComposer } = useIdeaComposer()
 *
 * // Open IdeaCreationDialog with preselected tag
 * editIdeaWithComposer({
 *   source:'create-board-ideaCard-card-1',
 *   ideaId: 'some-idea-id',
 * })
 *
 * const { createIdeaWithComposer } = useIdeaComposer()
 *
 * // Open IdeaCreationDialog with preselected tag
 * createIdeaWithComposer({
 *   source:'create-gallery-header-newButton-1',
 *   tags: ['some-tag-id'],
 * })
 *
 * // Open IdeaCreationDialog with AI generated text
 * createIdeaWithComposer({
 *   source:'create-gallery-ideaGenerator-newIdea-1'
 *   text: 'AI generated text',
 *   aiAssisted: 'true',
 * })
 *
 * // Open the IdeaCreationDialog with group and placeAfterId
 * createIdeaWithComposer({
 *   source:'create-board-group-newIdea-1'
 *
 *   groupId: 'Done',
 *   placeAfterId: 'some-idea-id',
 * })
 */
export const useIdeaComposer = (): UseIdeaComposerReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const organizationId = useOrganizationId()

  const queryParams = React.useMemo(
    () => parseQueryParams(location.search),
    [location.search],
  )

  const createIdeaWithComposer = React.useCallback<CreateIdeaWithComposer>(
    (input) => {
      const { duplicate, basePath, ...inputParams } = input
      const search = serializeQueryParams({
        ...queryParams,
        ...inputParams,
      })

      // When on the Ideas page (/create/ideas), navigate to "new" -> /create/ideas/new
      // From other pages (e.g., Feeds), navigate to "ideas/new" -> /create/feeds/.../ideas/new
      const isOnIdeasPage = location.pathname.startsWith('/create/ideas')
      const pathname = basePath
        ? `${basePath}/ideas/new`
        : isOnIdeasPage
          ? 'new'
          : 'ideas/new'
      navigate(
        { pathname, search },
        {
          state: {
            action: duplicate ? 'duplicate' : 'create',
            returnTo: location.pathname,
          },
        },
      )
      BufferTrackerReact.ideaComposerOpened({
        clientName: 'publishWeb',
        organizationId: organizationId ?? '',
        cta: input.source,
      })
    },
    [navigate, location.pathname, queryParams, organizationId],
  )

  const editIdeaWithComposer = React.useCallback(
    ({ ideaId, source }: { source: CTA; ideaId: string }): void => {
      const search = serializeQueryParams({
        ...queryParams,
        source,
      })

      // When on the Ideas page (/create/ideas), navigate to "123/edit" -> /create/ideas/123/edit
      // From other pages (e.g., Feeds), navigate to "ideas/123/edit" -> /create/feeds/.../ideas/123/edit
      const isOnIdeasPage = location.pathname.startsWith('/create/ideas')
      const pathname = isOnIdeasPage ? `${ideaId}/edit` : `ideas/${ideaId}/edit`

      navigate(
        {
          pathname,
          search,
        },
        {
          state: {
            basePath: location.pathname,
            action: 'edit',
          },
        },
      )
      BufferTrackerReact.ideaComposerOpened({
        clientName: 'publishWeb',
        organizationId: organizationId ?? '',
        ideaId,
        cta: source,
      })
    },
    [navigate, location, queryParams, organizationId],
  )

  return {
    createIdeaWithComposer,
    editIdeaWithComposer,
  }
}
